import { useCallback, useState } from 'react';
import type { ChatMessage } from '../components/ChatBox/Messages';
import type { DataResponse } from '../types/dataResponse';
import type { TabItem } from '../types/tabs';

export type ViewMode = "sessions" | "start" | "chat" | "tab";

type UseMessageManagerProps = {
  sessionId: string | null;
  userID: string | null;
};

// Extend DataResponse with a processed flag for client-side tracking
type ProcessableDataResponse = DataResponse & { _processed?: boolean };

// Helper function to deduplicate tabs
const deduplicateTabs = (tabs: TabItem[]): TabItem[] => {
  const seen = new Set<string>();
  return tabs.filter(tab => {
    // Only deduplicate flowsheet tabs (predictor_model and optimization always create new tabs)
    if (tab.type === 'flowsheet') {
      const key = `${tab.type}-${tab.file_name || 'untitled'}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
    }
    return true;
  });
};

// Map DataResponse to TabItem based on response type and tab_destination
const mapResponseToTabItem = (response: DataResponse): TabItem | null => {
  const tabDestination = response.tab_destination;
  const referenceId = response.reference_id;

  switch (response.type) {
    case 'flowsheet': {
      const flowsheetData = (response.data as any).payload;
      return {
        id: referenceId || `flowsheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file_name: 'Generated Flowsheet',
        file_type: 'flowsheet',
        type: 'flowsheet' as const,
        flowsheet_data: flowsheetData,
        images: []
      };
    }

    case 'model_graph': {
      const modelGraphData = (response.data as any).payload;
      return {
        id: referenceId || `predictor-model-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file_name: 'Predictor Model',
        file_type: 'model',
        type: 'predictor_model' as const,
        predictor_model_data: {
          model_name: 'Predictor Model',
          model_object: null,
          model_graph: modelGraphData
        },
        images: []
      };
    }

    case 'optimization': {
      const optimizationData = (response.data as any).payload;
      return {
        id: referenceId || `optimization-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file_name: optimizationData.problem_name || 'Optimization Problem',
        file_type: 'optimization',
        type: 'optimization' as const,
        optimization_data: optimizationData,
        images: []
      };
    }

    case 'file': {
      const fileData = response.data as any;
      if (fileData.file_type === 'table') {
        return {
          id: referenceId || `table-${fileData.file_name}-${Date.now()}`,
          file_name: fileData.file_name,
          file_type: 'table',
          type: 'table' as const,
          images: [],
          plots: []
        };
      } else if (fileData.file_type === 'pdf') {
        return {
          id: referenceId || `pdf-${fileData.file_name}-${Date.now()}`,
          file_name: fileData.file_name,
          file_type: 'pdf',
          type: 'pdf' as const,
          images: [],
          plots: [],
          pdf_data: {
            pdf_file_name: fileData.file_name,
            markdown_content: fileData.markdown_content || ''
          }
        };
      } else if (fileData.file_type === 'plot') {
        // Return appropriate data structure based on tab_destination
        if (tabDestination === 'modeling') {
          return {
            id: referenceId || `temp-${Date.now()}`,
            file_name: '',
            file_type: 'plot',
            type: 'predictor_model' as const,
            images: [{
              file_name: fileData.file_name,
              file_type: 'plot'
            }],
            plots: []
          };
        } else {
          return {
            id: referenceId || `temp-${Date.now()}`,
            file_name: '',
            file_type: 'plot',
            type: 'data' as const,
            images: [],
            plots: [{
              file_name: fileData.file_name,
              file_type: 'plot',
              plot_json: undefined
            }]
          };
        }
      }
      return null;
    }

    case 'plotly': {
      const plotlyData = response.data as any;
      const uniqueName = plotlyData.file_name || `Interactive Plot ${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      return {
        id: referenceId || `temp-${Date.now()}`,
        file_name: '',
        file_type: 'plot',
        type: 'data' as const,
        images: [],
        plots: [{
          file_name: uniqueName,
          file_type: 'plot',
          plot_json: plotlyData.payload
        }]
      };
    }

    default:
      return null;
  }
};

export function useMessageManager({ sessionId, userID }: UseMessageManagerProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("sessions");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [isTrainingInProgress, setIsTrainingInProgress] = useState(false);

  const updateMessages = useCallback((newMsgs: ChatMessage[]) => {
    setMessages((prev) => {
      // Create a map of existing messages by ID to deduplicate
      const messageMap = new Map<string, ChatMessage>();
      
      // Add existing messages first
      prev.forEach(msg => {
        if (msg.id) {
          messageMap.set(msg.id, msg);
        }
      });
      
      // Add or update with new messages
      newMsgs.forEach(msg => {
        if (msg.id) {
          messageMap.set(msg.id, msg);
        }
      });
      
      // Convert back to array and sort by creation time
      const deduplicated = Array.from(messageMap.values());
      return deduplicated.sort((a, b) => {
        const timeA = a.createdAt ? a.createdAt.getTime() : Date.now();
        const timeB = b.createdAt ? b.createdAt.getTime() : Date.now();
        return timeA - timeB;
      });
    });
  }, []);

  const handleResponse = useCallback((data: ProcessableDataResponse[]) => {
    console.log('MessageManager handling response:', data);
    
    if (!data || data.length === 0) return;

    // Filter out already processed items
    const newData = data.filter(item => !item._processed);
    if (newData.length === 0) return;

    setTabs((prev) => {
      let updatedTabs = [...prev];
      let newTabToActivate: TabItem | null = null;

      // Process each response
      for (const response of newData) {
        // Check if this response references an existing tab
        const targetTabIndex = response.reference_id 
          ? updatedTabs.findIndex(tab => tab.id === response.reference_id)
          : -1;

        if (targetTabIndex !== -1) {
          // Update existing tab - add to empty fields, overwrite non-empty fields
          const existingTab = updatedTabs[targetTabIndex];
          const newData = mapResponseToTabItem(response);
          
          if (newData) {
            updatedTabs[targetTabIndex] = {
              ...existingTab,
              ...newData,
              id: existingTab.id, // Keep original ID
              // For arrays: always merge (add)
              images: [...(existingTab.images || []), ...(newData.images || [])],
              plots: [...(existingTab.plots || []), ...(newData.plots || [])]
            };
            newTabToActivate = updatedTabs[targetTabIndex];
          }
        } else {
          // Create new tab
          const newTab = mapResponseToTabItem(response);
          
          if (newTab) {
            // Check if we should merge with an existing tab of the same type
            if (newTab.type === 'flowsheet') {
              const existingFlowsheet = updatedTabs.findIndex(tab => tab.type === 'flowsheet');
              if (existingFlowsheet !== -1) {
                updatedTabs[existingFlowsheet] = { ...newTab, id: updatedTabs[existingFlowsheet].id };
                newTabToActivate = updatedTabs[existingFlowsheet];
              } else {
                updatedTabs.push(newTab);
                newTabToActivate = newTab;
              }
            } else if (newTab.type === 'table') {
              const existingTable = updatedTabs.findIndex(tab => tab.type === 'table' && tab.file_name === newTab.file_name);
              if (existingTable !== -1) {
                updatedTabs[existingTable] = { ...newTab, id: updatedTabs[existingTable].id };
                newTabToActivate = updatedTabs[existingTable];
              } else {
                updatedTabs.push(newTab);
                newTabToActivate = newTab;
              }
            } else if (newTab.type === 'pdf') {
              // Each PDF gets its own tab
              updatedTabs.push(newTab);
              newTabToActivate = newTab;
            } else if (newTab.type === 'predictor_model') {
              // If it's a predictor_model with only images, add to existing predictor_model tab
              if (newTab.images && newTab.images.length > 0 && !newTab.predictor_model_data) {
                const existingPredictorModel = updatedTabs.findIndex(tab => tab.type === 'predictor_model');
                if (existingPredictorModel !== -1) {
                  const existingImages = updatedTabs[existingPredictorModel].images || [];
                  updatedTabs[existingPredictorModel] = {
                    ...updatedTabs[existingPredictorModel],
                    images: [...existingImages, ...newTab.images]
                  };
                  newTabToActivate = updatedTabs[existingPredictorModel];
                }
                // If no tab exists, images are dropped (shouldn't happen in practice)
              } else {
                // Full predictor model data, create new tab
                updatedTabs.push(newTab);
                newTabToActivate = newTab;
              }
            } else if (newTab.type === 'data') {
              // Aggregate plots into a single data tab
              const existingDataTab = updatedTabs.findIndex(tab => tab.type === 'data');
              const plotData = newTab.plots?.[0];
              
              if (existingDataTab !== -1 && plotData) {
                const existingPlots = updatedTabs[existingDataTab].plots || [];
                // Check for duplicates by comparing both file_name and plot_json content
                // Check for duplicates by comparing both file_name and plot_json content
                const isDuplicate = existingPlots.some(p => 
                  p.file_name === plotData.file_name && 
                  JSON.stringify(p.plot_json) === JSON.stringify(plotData.plot_json)
                );
                if (!isDuplicate) {
                  updatedTabs[existingDataTab] = {
                    ...updatedTabs[existingDataTab],
                    plots: [...existingPlots, plotData]
                  };
                }
                newTabToActivate = updatedTabs[existingDataTab];
              } else if (plotData) {
                const newDataTab: TabItem = {
                  id: `data-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  file_name: 'Data',
                  file_type: 'data',
                  type: 'data' as const,
                  images: [],
                  plots: [plotData]
                };
                updatedTabs.push(newDataTab);
                newTabToActivate = newDataTab;
              }
            } else {
              // optimization always creates new tabs
              updatedTabs.push(newTab);
              newTabToActivate = newTab;
            }
          }
          // Note: All response types are now handled by mapResponseToTabItem
          // No fallback cases needed - single source of truth for tab creation
        }
      }

      // Deduplicate and set active tab
      const finalTabs = deduplicateTabs(updatedTabs);
      if (newTabToActivate) {
        setActiveTabId(newTabToActivate.id);
      }

      return finalTabs;
    });

    // Mark all processed items
    newData.forEach(item => {
      item._processed = true;
    });

    // Check if training completed
    if (isTrainingInProgress && newData.some(item => {
      if (item.type === 'file') {
        const fileName = (item.data as any).file_name;
        return fileName && (
          fileName.toLowerCase().includes('training') || 
          fileName.toLowerCase().includes('model') ||
          fileName.toLowerCase().includes('mlp')
        );
      }
      return false;
    })) {
      setIsTrainingInProgress(false);
    }

    // Refresh table data if needed
    if (newData.some(item => item.type === 'file' && (item.data as any).file_type === 'table')) {
      setTableRefreshKey(prev => prev + 1);
    }

    // Determine next view
    const hasTabContent = newData.some(item => 
      item.type === 'flowsheet' || 
      item.type === 'model_graph' || 
      item.type === 'optimization' ||
      item.type === 'plotly' ||
      (item.type === 'file' && ((item.data as any).file_type === 'table' || (item.data as any).file_type === 'plot' || (item.data as any).file_type === 'pdf'))
    );

    if (hasTabContent && view !== "tab") {
      if (newData.some(item => item.type === 'file' && ((item.data as any).file_type === 'table' || (item.data as any).file_type === 'pdf'))) {
        setTimeout(() => setView("tab"), 100);
      } else {
        setView("tab");
      }
    }
  }, [isTrainingInProgress, view]);

  const sendMessage = useCallback(async (
    message: string, 
    files?: File[], 
    uploadFiles?: (message: string) => Promise<{ success: boolean; data?: any }>
  ) => {
    if (!message.trim() && (!files || files.length === 0)) return;
    if (!sessionId || !userID) return; // Ensure we have session info
    
    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      id: `user-${Date.now()}`,
      attachment: files ? [...files] : undefined,
      createdAt: new Date()
    };
    
    updateMessages([userMessage]);
    
    // Handle file uploads
    if (files && files.length > 0 && uploadFiles) {
      const result = await uploadFiles(message);
      if (result.success && result.data) {
        handleResponse(result.data);
      }
      return;
    }
    
    // For regular messages, the ChatBox useChat hook will handle the API call
    // This is just for consistent message state management
  }, [updateMessages, handleResponse, sessionId, userID]);

  const removeTab = useCallback((tabId: string) => {
    setTabs((prev) => {
      const filtered = prev.filter(tab => tab.id !== tabId);
      
      // If the removed tab was active, switch to another tab
      if (activeTabId === tabId && filtered.length > 0) {
        setActiveTabId(filtered[0].id);
      } else if (filtered.length === 0) {
        // If no tabs left, go back to chat view
        setView("chat");
        setActiveTabId(null);
      }
      
      return filtered;
    });
  }, [activeTabId]);


  return {
    // State
    messages,
    tabs,
    activeTabId,
    view,
    tableRefreshKey,
    isTrainingInProgress,
    
    // Actions
    updateMessages,
    handleResponse,
    sendMessage,
    setActiveTabId,
    setView,
    setIsTrainingInProgress,
    setMessages,
    setTabs,
    removeTab,
    
    // For external message updates
    addExternalMessage: updateMessages
  };
}
